import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  governanceCommandSchema,
  type BulkDeleteResult,
  type GovernanceCommandType,
} from "./schemas";
import { db } from "integrations/firebase/firestore";

export type GovernanceCommandState =
  | { status: "idle" }
  // `type` rides every state after a submit so the feedback strip can say
  // what the command did rather than a generic "Command succeeded".
  | { status: "submitting"; type: GovernanceCommandType }
  | { status: "watching"; type: GovernanceCommandType; commandStatus: string }
  // `result` is carried through for the bulk commands, whose outcome is a
  // per-target report rather than a single success. `mint` carries the
  // MINT_INVITE outcome — its raw token, which the invite document itself
  // does not store. Absent for every command whose only outcome is that it
  // applied.
  | {
      status: "succeeded";
      type: GovernanceCommandType;
      result?: BulkDeleteResult;
      mint?: { token: string };
    }
  | { status: "failed"; error: string };

export function useGovernanceCommand() {
  const [state, setState] = useState<GovernanceCommandState>({
    status: "idle",
  });
  const unsubRef = useRef<(() => void) | null>(null);
  // Incremented on every submit and on unmount. An in-flight submit whose
  // generation is stale must not publish state or install its listener —
  // otherwise it overwrites a newer submit's listener and leaks it.
  const generationRef = useRef(0);

  useEffect(() => {
    return () => {
      generationRef.current += 1;
      unsubRef.current?.();
      unsubRef.current = null;
    };
  }, []);

  const submit = useCallback(
    async (
      type: GovernanceCommandType,
      uid: string,
      payload: Record<string, unknown>,
    ) => {
      unsubRef.current?.();
      unsubRef.current = null;
      generationRef.current += 1;
      const generation = generationRef.current;
      const isCurrent = () => generationRef.current === generation;

      setState({ status: "submitting", type });

      try {
        const docRef = await addDoc(collection(db, "governance_commands"), {
          type,
          requestedBy: uid,
          requestedAt: serverTimestamp(),
          status: "REQUESTED",
          payload,
        });

        if (!isCurrent()) return;
        setState({ status: "watching", type, commandStatus: "REQUESTED" });

        const unsub = onSnapshot(
          doc(db, "governance_commands", docRef.id),
          (snap) => {
            if (!isCurrent()) return;
            if (!snap.exists()) return;
            const result = governanceCommandSchema.safeParse(snap.data());
            if (!result.success) return;

            const cmd = result.data;
            if (cmd.status === "SUCCEEDED") {
              unsub();
              unsubRef.current = null;
              setState({
                status: "succeeded",
                type,
                result: cmd.result,
                // Only MINT_INVITE settles with a token, and this is the one
                // moment it exists on a client: the document id is its hash,
                // so a listing can never show it again.
                mint:
                  cmd.type === "MINT_INVITE" && cmd.token
                    ? { token: cmd.token }
                    : undefined,
              });
            } else if (cmd.status === "FAILED") {
              unsub();
              unsubRef.current = null;
              setState({
                status: "failed",
                error: cmd.error ?? "Command failed",
              });
            } else {
              setState({ status: "watching", type, commandStatus: cmd.status });
            }
          },
          (err) => {
            unsub();
            if (!isCurrent()) return;
            unsubRef.current = null;
            setState({
              status: "failed",
              error:
                err instanceof Error ? err.message : "Failed to watch command",
            });
          },
        );

        // A newer submit (or unmount) landed while addDoc was in flight.
        if (!isCurrent()) {
          unsub();
          return;
        }
        unsubRef.current = unsub;
      } catch (err) {
        if (!isCurrent()) return;
        setState({
          status: "failed",
          error:
            err instanceof Error ? err.message : "Failed to submit command",
        });
      }
    },
    [],
  );

  const reset = useCallback(() => {
    generationRef.current += 1;
    unsubRef.current?.();
    unsubRef.current = null;
    setState({ status: "idle" });
  }, []);

  return { state, submit, reset };
}
